import React from 'react';

// Identity details below MUST stay in sync with the Google Play store listing
// (app name, package name, developer name and contact emails). Google Play
// rejects the policy if these do not match the listing.
const APP_NAME = 'BG';
const PACKAGE_NAME = 'com.bombay365';
const DEVELOPER_NAME = 'Malkeet Kaur';
const DEVELOPER_EMAIL = 'delhi365official@gmail.com';
const SUPPORT_EMAIL = 'bombay365gems@gmail.com';
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${PACKAGE_NAME}`;
const WEBSITE_URL = 'https://bombay365.in';
const LAST_UPDATED = '3 August 2026';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-md mt-10 text-gray-800">
      <h1 className="text-3xl font-bold mb-2">
        Privacy Policy for {APP_NAME} ({PACKAGE_NAME})
      </h1>
      <p className="text-sm text-gray-600 mb-6">
        Last updated: {LAST_UPDATED}
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">1. Who We Are</h2>
      <p className="mb-4">
        This Privacy Policy applies to the mobile application{' '}
        <strong>{APP_NAME}</strong>, published on Google Play under the package
        name <strong>{PACKAGE_NAME}</strong>, and to our website at{' '}
        <a
          href={WEBSITE_URL}
          className="text-blue-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {WEBSITE_URL}
        </a>
        .
      </p>
      <p className="mb-4">
        The {APP_NAME} app is developed, owned and operated by{' '}
        <strong>{DEVELOPER_NAME}</strong>, an individual developer based in
        India, who is the developer of record for this app on Google Play and
        the data controller responsible for the personal information described
        in this policy. References to &ldquo;we&rdquo;, &ldquo;us&rdquo;,
        &ldquo;our&rdquo; and &ldquo;the developer&rdquo; in this policy mean{' '}
        {DEVELOPER_NAME}.
      </p>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>
          <strong>App name (Google Play listing):</strong> {APP_NAME}
        </li>
        <li>
          <strong>Package / application ID:</strong> {PACKAGE_NAME}
        </li>
        <li>
          <strong>Google Play listing:</strong>{' '}
          <a
            href={PLAY_STORE_URL}
            className="text-blue-600 underline break-all"
            target="_blank"
            rel="noopener noreferrer"
          >
            {PLAY_STORE_URL}
          </a>
        </li>
        <li>
          <strong>Developer / legal entity:</strong> {DEVELOPER_NAME} (India)
        </li>
        <li>
          <strong>Developer email:</strong>{' '}
          <a href={`mailto:${DEVELOPER_EMAIL}`} className="text-blue-600 underline">
            {DEVELOPER_EMAIL}
          </a>
        </li>
        <li>
          <strong>App support email:</strong>{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">
            {SUPPORT_EMAIL}
          </a>
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">2. Scope of This Policy</h2>
      <p className="mb-4">
        This policy explains what information the {APP_NAME} app and website
        collect, how that information is used, who it is shared with, how long
        it is kept, and the choices and rights you have. It applies to every
        user of the {APP_NAME} app and website. By creating an account or using
        the app, you agree to the practices described here.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        3. Information We Collect
      </h2>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>
          <strong>Account information:</strong> your name, mobile number, email
          address, and (where you provide it) your postal address and PIN code,
          used to create and secure your account.
        </li>
        <li>
          <strong>Verification data:</strong> one-time passwords (OTPs) sent to
          your mobile number to verify that the account belongs to you.
        </li>
        <li>
          <strong>Wallet and transaction information:</strong> the amounts you
          add or withdraw, your wallet balance, transaction history, and the
          payment details you supply for withdrawals (for example the UPI ID or
          mobile number linked to Google Pay, Paytm or PhonePe). Payments are
          processed by the relevant payment provider; we do not collect or store
          full card numbers, CVV codes, UPI PINs or bank passwords.
        </li>
        <li>
          <strong>Activity within the app:</strong> the entries you place,
          results, winnings, commission and related history, so that we can
          operate the app and show you your own records.
        </li>
        <li>
          <strong>Support communications:</strong> messages, screenshots and
          contact details you send us through in-app help, WhatsApp or email.
        </li>
        <li>
          <strong>Device and technical information:</strong> device model,
          operating system version, app version, language, approximate
          region/IP-derived location, crash logs and app usage events collected
          through Google Firebase and Google Analytics.
        </li>
        <li>
          <strong>Push notification token:</strong> a Firebase Cloud Messaging
          registration token so we can send you account, result and service
          notifications. You can turn notifications off at any time in your
          device settings.
        </li>
        <li>
          <strong>Cookies and local storage:</strong> on our website we use
          cookies and browser storage to keep you signed in and remember your
          preferences.
        </li>
      </ul>
      <p className="mb-4">
        We do not collect your precise GPS location, your contacts, your photos
        beyond files you deliberately upload to support, your call logs or your
        SMS messages.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        4. How We Use Your Information
      </h2>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>To create, verify, secure and manage your account.</li>
        <li>
          To operate the core features of the app, including your wallet,
          deposits, withdrawals, entries, results and history.
        </li>
        <li>To provide customer support and respond to your requests.</li>
        <li>
          To send service messages such as account, transaction and result
          notifications, and — where you have consented — promotional messages.
        </li>
        <li>
          To detect, investigate and prevent fraud, abuse, duplicate accounts
          and other misuse of the service.
        </li>
        <li>
          To monitor performance, diagnose crashes and improve the app through
          aggregated analytics.
        </li>
        <li>To comply with applicable laws and lawful requests.</li>
      </ul>
      <p className="mb-4">
        We do not use your personal information for automated decision-making
        that produces legal effects about you, and we do not sell your personal
        information.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        5. Sharing and Disclosure
      </h2>
      <p className="mb-4">
        We do not sell, rent or trade your personal information. We share it
        only in these limited situations:
      </p>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>
          <strong>Payment providers:</strong> the UPI and wallet services you
          choose (such as Google Pay, Paytm or PhonePe) and our payment partners
          receive the details needed to complete your deposit or withdrawal.
        </li>
        <li>
          <strong>Infrastructure and analytics providers:</strong> Google
          Firebase and Google Analytics process device, usage, crash and
          notification data on our behalf. See{' '}
          <a
            href="https://policies.google.com/privacy"
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google&rsquo;s Privacy Policy
          </a>
          .
        </li>
        <li>
          <strong>Service providers:</strong> hosting, SMS/OTP delivery and
          customer support tools, which may process your data only on our
          instructions and under confidentiality obligations.
        </li>
        <li>
          <strong>Legal and safety:</strong> courts, regulators or law
          enforcement where disclosure is required by law, or where it is
          necessary to protect our rights, our users or the public.
        </li>
        <li>
          <strong>Business transfer:</strong> if the app or business is
          transferred to another owner, your information may be transferred as
          part of that transaction, subject to this policy.
        </li>
      </ul>

      <h2 className="text-xl font-semibold mt-6 mb-2">6. Data Security</h2>
      <p className="mb-4">
        Data is transmitted over encrypted HTTPS connections and stored on
        access-controlled servers. We apply technical and organisational
        safeguards appropriate to the sensitivity of the data, including
        restricted administrative access and authentication controls. No method
        of transmission or storage is completely secure, so we cannot guarantee
        absolute security; if a breach affects your personal data we will notify
        you and the relevant authorities as required by law.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">7. Data Retention</h2>
      <p className="mb-4">
        We keep your account and transaction information for as long as your
        account is active, and afterwards only for as long as needed to meet
        legal, accounting, tax, dispute-resolution and fraud-prevention
        obligations. Transaction records may be retained for the period required
        by Indian financial record-keeping law. Once no longer required, data is
        deleted or irreversibly anonymised.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        8. Account and Data Deletion
      </h2>
      <p className="mb-4">
        You can request deletion of your {APP_NAME} account and associated
        personal data at any time:
      </p>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>
          In the app or on our website, open the{' '}
          <a href="/delete-account" className="text-blue-600 underline">
            Delete Account
          </a>{' '}
          page (
          <span className="break-all">{WEBSITE_URL}/delete-account</span>) and
          submit a deletion request; or
        </li>
        <li>
          Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">
            {SUPPORT_EMAIL}
          </a>{' '}
          from your registered email address, or with your registered mobile
          number, asking us to delete your account.
        </li>
      </ul>
      <p className="mb-4">
        We action verified deletion requests within 30 days. Your profile,
        contact details, activity history and push notification token are
        deleted; records we are legally required to keep (such as financial
        transaction records) are retained for the statutory period and then
        deleted. Please withdraw any remaining wallet balance before requesting
        deletion, as deletion is permanent and cannot be reversed.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">9. Your Rights</h2>
      <p className="mb-4">
        Subject to applicable law, including the Digital Personal Data
        Protection Act, 2023 in India, you may:
      </p>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>Access the personal information we hold about you.</li>
        <li>Correct information that is inaccurate or incomplete.</li>
        <li>Request deletion of your account and personal data.</li>
        <li>Withdraw consent for promotional messages or notifications.</li>
        <li>Complain to the relevant data protection authority.</li>
      </ul>
      <p className="mb-4">
        To exercise any of these rights, email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">
          {SUPPORT_EMAIL}
        </a>
        . We respond within 30 days and may ask you to verify your identity
        first.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        10. Children&rsquo;s Privacy
      </h2>
      <p className="mb-4">
        {APP_NAME} is intended only for adults aged 18 years and above. It is
        not directed at children, and we do not knowingly collect personal
        information from anyone under 18. If we learn that we have collected
        such information, we will delete the account and the data promptly. If
        you believe a minor has provided us with personal information, contact
        us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">
          {SUPPORT_EMAIL}
        </a>
        .
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        11. Third-Party Links and Services
      </h2>
      <p className="mb-4">
        The app and website may link to third-party sites and services (for
        example Google Play, payment providers or WhatsApp support). We are not
        responsible for their content or privacy practices, and we encourage you
        to read their privacy policies before sharing information with them.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        12. International Data Transfers
      </h2>
      <p className="mb-4">
        We are based in India and our servers are located in India. Some
        providers we rely on, such as Google Firebase, may process data on
        servers outside India under their own safeguards and contractual
        commitments.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">
        13. Changes to This Policy
      </h2>
      <p className="mb-4">
        We may update this Privacy Policy from time to time. The
        &ldquo;Last updated&rdquo; date at the top of this page shows when it
        was last revised. Material changes will be communicated in the app, on
        this page, or by email. Continued use of {APP_NAME} after an update
        means you accept the revised policy.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">14. Contact Us</h2>
      <p className="mb-2">
        For any question about this policy, your data, or to exercise your
        rights, contact the developer of {APP_NAME}:
      </p>
      <ul className="list-disc list-inside space-y-1 mb-4">
        <li>
          <strong>Developer:</strong> {DEVELOPER_NAME}, India
        </li>
        <li>
          <strong>App:</strong> {APP_NAME} ({PACKAGE_NAME}) on Google Play
        </li>
        <li>
          <strong>Support email:</strong>{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">
            {SUPPORT_EMAIL}
          </a>
        </li>
        <li>
          <strong>Developer email:</strong>{' '}
          <a href={`mailto:${DEVELOPER_EMAIL}`} className="text-blue-600 underline">
            {DEVELOPER_EMAIL}
          </a>
        </li>
        <li>
          <strong>Website:</strong>{' '}
          <a
            href={WEBSITE_URL}
            className="text-blue-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {WEBSITE_URL}
          </a>
        </li>
      </ul>
    </div>
  );
};

export default PrivacyPolicy;
